import type { Locale } from "./config";

/** Locale-aware formatting helpers. Currency stays GBP for every locale. */
export function formatters(locale: Locale) {
  return {
    /** Pence -> "£1,250.00" / "£1,250" when whole. */
    formatMoney(pence: number | null | undefined): string {
      const p = pence ?? 0;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: p % 100 === 0 ? 0 : 2,
      }).format(p / 100);
    },
    formatNumber(value: number): string {
      return new Intl.NumberFormat(locale).format(value);
    },
    /** "31 July 2026" / "2026年7月31日" */
    formatDate(value: string | number | Date): string {
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(value));
    },
    /** 24-hour clock in every supported locale: "18:30". */
    formatTime(value: string | number | Date): string {
      return new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(value));
    },
    formatDateTime(value: string | number | Date): string {
      const d = new Date(value);
      return `${new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(d)} ${new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(d)}`;
    },
    /** "in 3 hours" / "3時間後" — never used for authoritative auction timing. */
    formatRelative(value: string | number | Date): string {
      const diffMs = new Date(value).getTime() - Date.now();
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
      const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
        ["day", 86_400_000],
        ["hour", 3_600_000],
        ["minute", 60_000],
        ["second", 1000],
      ];
      for (const [unit, ms] of units) {
        if (Math.abs(diffMs) >= ms || unit === "second") {
          return rtf.format(Math.round(diffMs / ms), unit);
        }
      }
      return "";
    },
  };
}
